/**
 * MathUtilsService - Shared mathematical utility functions
 *
 * Consolidates commonly used mathematical operations across the AASHTO calculation services.
 * Eliminates code duplication and provides a single source of truth for mathematical utilities.
 */

import * as math from 'mathjs';
import { jStat } from 'jstat';

/**
 * Shared mathematical utilities for AASHTO pavement design calculations
 */
export class MathUtilsService {
    /**
     * Calculates the Standard Normal Deviate (Zr) from a given reliability percentage.
     * This is equivalent to Excel's NORMSINV function.
     *
     * Replaces deprecated implementations from:
     * - AASHTOStructuralNumberCalculator.calculateZr() [MIGRATED]
     * - MathUtilsService.calculateZr() [DEPRECATED - removed in Phase 2]
     * - MathUtilsService.calculateZR() [DEPRECATED - removed in Phase 2]
     * - ExcelFormulaEngine.calculateZR() [DEPRECATED - bridge method]
     *
     * @param reliability The reliability percentage (e.g., 95 for 95%)
     * @returns The standard normal deviate (Zr)
     * @throws Error if reliability is not between 0 and 100
     */
    public static calculateStandardNormalDeviate(reliability: number): number {
        if (reliability <= 0 || reliability >= 100) {
            throw new Error('Reliability must be between 0 and 100.');
        }

        // For AASHTO calculations, we need the Z-score for the probability of failure
        // For 95% reliability, we need the Z-score for the lower 5% tail
        const probabilityOfFailure = (100 - reliability) / 100;

        // Use jStat to calculate inverse normal (equivalent to Excel's NORMSINV)
        return jStat.normal.inv(probabilityOfFailure, 0, 1);
    }

    /**
     * Safe logarithm base 10 calculation
     *
     * Consolidates the repeated `const log10 = (x: number) => math.log10(x)` pattern
     * found throughout the codebase.
     *
     * @param value The value to calculate log10 for
     * @returns The base-10 logarithm of the value
     * @throws Error if value is not positive
     */
    public static log10(value: number): number {
        if (value <= 0) {
            throw new Error(`log10: value must be positive, got ${value}`);
        }
        return math.log10(value);
    }

    /**
     * Calculate multiple log10 values at once
     * Utility for batch operations commonly seen in AASHTO calculations
     *
     * @param values Array of values to calculate log10 for
     * @returns Array of log10 results
     */
    public static log10Batch(values: number[]): number[] {
        return values.map(value => this.log10(value));
    }

    /**
     * Calculate power with input validation
     * Common pattern seen in AASHTO serviceability calculations
     *
     * @param base The base value
     * @param exponent The exponent
     * @returns base^exponent
     */
    public static power(base: number, exponent: number): number {
        if (!isFinite(base) || !isFinite(exponent)) {
            throw new Error(`Power calculation: invalid inputs base=${base}, exponent=${exponent}`);
        }
        return Math.pow(base, exponent);
    }

    /**
     * Clamp a value between min and max bounds
     * Common pattern for ensuring values stay within engineering limits
     *
     * @param value The value to clamp
     * @param min Minimum allowed value
     * @param max Maximum allowed value
     * @returns Clamped value
     */
    public static clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
    }

    /**
     * Round to specified decimal places
     * Standardizes rounding across the application
     *
     * @param value The value to round
     * @param decimals Number of decimal places (default: 6 for engineering precision)
     * @returns Rounded value
     */
    public static round(value: number, decimals = 6): number {
        const factor = Math.pow(10, decimals);
        return Math.round(value * factor) / factor;
    }

    /**
     * Check if a number is within a tolerance of another number
     * Useful for convergence checks in iterative calculations
     *
     * @param value1 First value
     * @param value2 Second value
     * @param tolerance Tolerance (default: 1e-9)
     * @returns True if values are within tolerance
     */
    public static isWithinTolerance(value1: number, value2: number, tolerance = 1e-9): boolean {
        return Math.abs(value1 - value2) <= tolerance;
    }

    /**
     * Validate that a number is finite and not NaN
     * Common validation pattern throughout the codebase
     *
     * @param value The value to validate
     * @param parameterName Name of the parameter for error messages
     * @throws Error if value is not a valid finite number
     */
    public static validateFiniteNumber(value: number, parameterName: string): void {
        if (!isFinite(value) || isNaN(value)) {
            throw new Error(`${parameterName} must be a finite number, got ${value}`);
        }
    }
}

/**
 * Singleton instance for global access
 * Provides backward compatibility for existing code patterns
 */
export const mathUtilsService = new MathUtilsService();

/**
 * Re-export static methods for convenience
 * Allows direct import of functions: import { log10, calculateStandardNormalDeviate } from './MathUtilsService'
 */
export const { calculateStandardNormalDeviate, log10, log10Batch, power, clamp, round, isWithinTolerance, validateFiniteNumber } = MathUtilsService;
